import React, {
    useState,
    useEffect,
    useContext,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    Button,
    ModalNext,
    ModalHeader,
    ModalBody,
    ModalFooter,
} from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { ViewModelForm, useDataRefresh, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { useValidation } from 'gw-portals-validation-react';
import { ServiceRequestService } from 'gw-capability-servicerequest';
import { useAuthentication } from 'gw-digital-auth-react';
import messages from './ReplyMessagePopup.messages';
import metadata from './ReplyMessagePopup.metadata.json5';

function ReplyMessagePopup(props) {
    const [messageVM, setMessageVM] = useState({});
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();
    const { isComponentValid, onValidate } = useValidation('ReplyMessagePopup');
    const {
        isOpen,
        onReject,
        onResolve,
        onAfterClose,
        serviceRequestNumber,
        messageData
    } = props;


    useEffect(() => {
        setMessageVM(
            viewModelService.create(
                {
                    title: _.get(messageData, 'title'),
                    type: _.get(messageData, 'type'),
                },
                'cc',
                'edge.capabilities.servicerequest.message.dto.ServiceRequestMessageDTO'
            )
        );
    // only execute once
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleReplyMessage = useCallback(async () => {
        messageVM.sendDate.value = new Date();
        const replyMessage = await ServiceRequestService.createMessage(
            serviceRequestNumber,
            messageVM.value,
            authHeader
        );
        onResolve(replyMessage);
    }, [authHeader, messageVM, onResolve, serviceRequestNumber]);

    const handleClose = useCallback(() => {
        onReject();
    }, [onReject]);

    const handleMessageVMChange = useCallback((newModel) => {
        setMessageVM(newModel);
        refreshData();
    }, [refreshData]);


    if (_.isEmpty(messageVM)) {
        return null;
    }

    const overrides = {
        previousMessage: {
            value: _.get(messageData, 'body')
        }
    };

    const resolvers = {
        resolveCallbackMap: {
            onValidate: onValidate
        }
    };

    return (
        <ModalNext isOpen={isOpen} onRequestClose={handleClose} onAfterClose={onAfterClose}>
            <ModalHeader title={_.get(messageData, 'title')} />
            <ModalBody>
                <ViewModelForm
                    uiProps={metadata.componentContent.content}
                    model={messageVM}
                    onValidationChange={onValidate}
                    onModelChange={handleMessageVMChange}
                    overrideProps={overrides}
                    callbackMap={resolvers.resolveCallbackMap}
                />
            </ModalBody>
            <ModalFooter>
                <Button
                    type="tertiary"
                    onClick={handleClose}
                >
                    {translator(messages.cancel)}
                </Button>
                <Button
                    onClick={handleReplyMessage}
                    disabled={!isComponentValid}
                >
                    {translator(messages.replyButton)}
                </Button>
            </ModalFooter>
        </ModalNext>
    );
}

ReplyMessagePopup.propTypes = {
    isOpen: PropTypes.bool,
    onResolve: PropTypes.func.isRequired,
    onReject: PropTypes.func.isRequired,
    onAfterClose: PropTypes.func.isRequired,
    messageData: PropTypes.shape({}),
    serviceRequestNumber: PropTypes.string.isRequired,
    messageVM: PropTypes.shape({
        type: PropTypes.shape({
            value: PropTypes.shape({})
        }),
        subject: PropTypes.shape({}),
        sendDate: PropTypes.shape({
            value: PropTypes.shape({})
        }),
        body: PropTypes.shape({}),
        value: PropTypes.shape({})
    })
};

ReplyMessagePopup.defaultProps = {
    isOpen: false,
    messageData: {},
    messageVM: undefined
};

export default ReplyMessagePopup;
